import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Button from '../UI/Button';
import { ButtonTypes } from '../../utils/Constants';
import usePageTitle from '../../utils/usePageTitle';

const Signup: React.FC = () => {
    usePageTitle('Sign up');
    const navigate = useNavigate();
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [username, setUsername] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [errors, setErrors] = useState<{
        first_name?: string; last_name?: string; username?: string; email?: string; password?: string; general?: string;
    }>({});
    const [submitting, setSubmitting] = useState(false);

    const validate = () => {
        const e: typeof errors = {};
        if (!firstName.trim()) e.first_name = "First name is required.";
        if (!lastName.trim()) e.last_name = "Last name is required.";
        if (!username.trim()) e.username = "Username is required.";
        if (!email.trim()) e.email = "Email is required.";
        else if (!/^\S+@\S+\.\S+$/.test(email)) e.email = "Enter a valid email.";
        if (!password) e.password = "Password is required.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        try {
            setSubmitting(true);
            const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/sign-up/`, { first_name: firstName, last_name: lastName, username, email, password });
            console.log('Signed up successfully:', response.data);
            navigate("/login")
        } catch (error: any) {
            if (axios.isAxiosError(error) && error.response) {
                const { status, data } = error.response;
                if (status === 400 && data && typeof data === "object") {
                    setErrors({
                        first_name: data.first_name?.[0],
                        last_name: data.last_name?.[0],
                        username: data.username?.[0],
                        email: data.email?.[0],
                        password: data.password?.[0],
                        general: data.detail || undefined,
                    });
                } else {
                    setErrors({ general: "Something went wrong. Please try again." });
                }
            } else {
                setErrors({ general: "Network error. Please try again." });
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen h-full bg-secondary">
            <div className="w-[90%] max-w-md my-4 p-8 bg-white rounded-lg shadow-lg">
                <h2 className="mb-4 text-sm font-medium text-gray-500">LET'S GET YOU STARTED</h2>
                <h3 className="mb-6 text-2xl font-semibold text-gray-800">Create an account</h3>

                {errors.general && (
                    <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700 border border-red-200">
                        {errors.general}
                    </div>
                )}

                <div className="mb-4">
                    <label className="block mb-2 text-sm font-medium text-gray-700" htmlFor="firstName">First name</label>
                    <input
                        type="text"
                        id="firstName"
                        value={firstName}
                        onChange={(e) => { setFirstName(e.target.value); if (errors.first_name) setErrors(p => ({ ...p, first_name: undefined })) }}
                        className={`w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary ${errors.first_name ? "border-red-400" : ""}`}
                        placeholder="First name"
                    />
                    {errors.first_name && <p className="mt-1 text-xs text-red-600">{errors.first_name}</p>}
                </div>
                <div className="mb-4">
                    <label className="block mb-2 text-sm font-medium text-gray-700" htmlFor="lastName">Last name</label>
                    <input
                        type="text"
                        id="lastName"
                        value={lastName}
                        onChange={(e) => { setLastName(e.target.value); if (errors.last_name) setErrors(p => ({ ...p, last_name: undefined })) }}
                        className={`w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary ${errors.last_name ? "border-red-400" : ""}`}
                        placeholder="Last name"
                    />
                    {errors.last_name && <p className="mt-1 text-xs text-red-600">{errors.last_name}</p>}
                </div>
                <div className="mb-4">
                    <label className="block mb-2 text-sm font-medium text-gray-700" htmlFor="username">Username</label>
                    <input
                        type="text"
                        id="username"
                        value={username}
                        onChange={(e) => { setUsername(e.target.value); if (errors.username) setErrors(p => ({ ...p, username: undefined })) }}
                        className={`w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary ${errors.username ? "border-red-400" : ""}`}
                        placeholder="Username"
                    />
                    {errors.username && <p className="mt-1 text-xs text-red-600">{errors.username}</p>}
                </div>
                <div className="mb-4">
                    <label className="block mb-2 text-sm font-medium text-gray-700" htmlFor="email">Email</label>
                    <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors(p => ({ ...p, email: undefined })) }}
                        className={`w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary ${errors.email ? "border-red-400" : ""}`}
                        placeholder="Email"
                    />
                    {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                </div>
                <div className="mb-6">
                    <label className="block mb-2 text-sm font-medium text-gray-700" htmlFor="password">Password</label>
                    <input
                        type="password"
                        id="password"
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); if (errors.password) setErrors(p => ({ ...p, password: undefined })) }}
                        className={`w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary ${errors.password ? "border-red-400" : ""}`}
                        placeholder="Password"
                    />
                    {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password}</p>}
                </div>
                <Button type={ButtonTypes.PRIMARY} onClick={handleSubmit} className="w-full !px-4 !py-2 font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a423a]" disabled={submitting}>
                    {submitting ? "Creating account..." : "Sign up"}
                </Button>
                <p className="mt-4 text-sm text-center text-gray-600">
                    Already have an account? <a href="/login" className="font-semibold text-gray-800 hover:underline">LOGIN HERE</a>
                </p>
            </div>
        </div>
    );
};

export default Signup;
